import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Stack, Typography, Paper, Button, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  ToggleButton, ToggleButtonGroup, TextField, InputAdornment, Tooltip,
} from "@mui/material";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { apiGet, apiPost, ApiError } from "../api/client";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

const money = (n) => `$${Number(n || 0).toFixed(0)}`;

function formatRelative(unix) {
  if (!unix) return "—";
  const ms = Number(unix) * 1000;
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

const STATUS_TABS = [
  { id: "queued",   label: "Queued",   color: "warning" },
  { id: "exported", label: "Exported", color: "primary" },
  { id: "listed",   label: "Listed",   color: "success" },
  { id: "error",    label: "Error",    color: "error"   },
];

function StatusChip({ status }) {
  const tab = STATUS_TABS.find((t) => t.id === status);
  if (!tab) return <Chip size="small" label={status || "—"} variant="outlined" sx={{ height: 22 }} />;
  return <Chip size="small" label={tab.label} color={tab.color} variant="outlined" sx={{ height: 22 }} />;
}

export default function FBQueue() {
  const { error: toastError, success: toastSuccess } = useToast();

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ queued: 0, exported: 0, listed: 0, error: 0 });
  const [statusTab, setStatusTab] = useState("queued");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState({}); // lot_id -> action

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      // Always pull the full queue so the counts header reflects every bucket;
      // we filter client-side by the active tab. With at most a few hundred
      // rows this is cheaper than re-hitting the API on every tab switch.
      const js = await apiGet("/api/fb_queue", { signal });
      if (signal?.aborted) return;
      setRows(Array.isArray(js?.queue) ? js.queue : []);
      setCounts(js?.counts || { queued: 0, exported: 0, listed: 0, error: 0 });
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load FB queue", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const filtered = rows.filter((r) => r.status === statusTab);
    if (!term) return filtered;
    return filtered.filter((r) =>
      (r.cleaned_title || "").toLowerCase().includes(term) ||
      (r.title || "").toLowerCase().includes(term) ||
      (r.lot_id || "").toLowerCase().includes(term)
    );
  }, [rows, statusTab, term]);

  const setRowAction = (lotId, action) =>
    setRowBusy((prev) => {
      const n = { ...prev };
      if (action) n[lotId] = action;
      else delete n[lotId];
      return n;
    });

  const setStatus = async (lotId, status) => {
    setRowAction(lotId, status);
    try {
      const res = await apiPost("/api/fb_queue/status", { lot_ids: [lotId], status });
      if (res?.success) {
        toastSuccess("Updated", `${lotId} → ${status}`);
        // Local patch: move the row to the new bucket without refetching.
        setRows((cur) => cur.map((r) => r.lot_id === lotId ? { ...r, status, updated_at: Math.floor(Date.now() / 1000) } : r));
        setCounts((cur) => {
          const next = { ...cur };
          const prev = rows.find((r) => r.lot_id === lotId)?.status;
          if (prev && next[prev] != null) next[prev] = Math.max(0, next[prev] - 1);
          if (next[status] != null) next[status] += 1;
          return next;
        });
      } else {
        toastError("Couldn't update", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't update", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRowAction(lotId, null);
    }
  };

  // Publish a single queue row via /api/fb_publish (async). The backend
  // spawns a detached subprocess and returns 202 immediately; we poll
  // /api/fb_publish/status every 5s until fb_queue flips to 'listed' or
  // 'error'. Spinner stays up the whole time so the operator sees
  // progress.
  const publishOne = async (row) => {
    setRowAction(row.lot_id, "publish");
    let res;
    try {
      res = await apiPost("/api/fb_publish", { lot_id: row.lot_id, price: row.price });
    } catch (e) {
      toastError("Couldn't start publish", e instanceof ApiError ? e.message : String(e));
      setRowAction(row.lot_id, null);
      return;
    }
    if (!res?.success) {
      toastError("Couldn't start publish", res?.error || "Unknown error");
      setRowAction(row.lot_id, null);
      return;
    }

    const prevStatus = row.status;
    setRows((cur) => cur.map((r) => r.lot_id === row.lot_id ? { ...r, status: "queued", updated_at: Math.floor(Date.now() / 1000) } : r));

    const start = Date.now();
    const finish = (newStatus, ok) => {
      setRows((cur) => cur.map((r) => r.lot_id === row.lot_id ? { ...r, status: newStatus, updated_at: Math.floor(Date.now() / 1000) } : r));
      setCounts((cur) => {
        const next = { ...cur };
        if (prevStatus && next[prevStatus] != null) next[prevStatus] = Math.max(0, next[prevStatus] - 1);
        if (next[newStatus] != null) next[newStatus] = (next[newStatus] || 0) + 1;
        return next;
      });
      if (ok) toastSuccess("Published to FB", row.lot_id);
      else toastError("Publish failed", `${row.lot_id} — check fb_publish_logs/`);
      setRowAction(row.lot_id, null);
    };

    const poll = async () => {
      try {
        const s = await apiGet(`/api/fb_publish/status?lot_id=${encodeURIComponent(row.lot_id)}`);
        if (s?.status === "listed" || s?.fb_listed) return finish("listed", true);
        if (s?.status === "error") return finish("error", false);
      } catch {
        // transient — keep polling
      }
      if (Date.now() - start > 10 * 60 * 1000) {
        toastError("Publish timed out", `${row.lot_id} — check fb_publish_logs/`);
        setRowAction(row.lot_id, null);
        return;
      }
      setTimeout(poll, 5000);
    };
    setTimeout(poll, 5000);
  };

  const remove = async (lotId) => {
    setRowAction(lotId, "remove");
    try {
      const res = await apiPost("/api/fb_queue/remove", { lot_id: lotId });
      if (res?.success) {
        toastSuccess("Removed", `${lotId} dropped from queue`);
        const prevStatus = rows.find((r) => r.lot_id === lotId)?.status;
        setRows((cur) => cur.filter((r) => r.lot_id !== lotId));
        if (prevStatus) {
          setCounts((cur) => ({ ...cur, [prevStatus]: Math.max(0, (cur[prevStatus] || 0) - 1) }));
        }
      } else {
        toastError("Couldn't remove", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't remove", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRowAction(lotId, null);
    }
  };

  const totalActionable = counts.queued + counts.error;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
            <FacebookOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="h1" sx={{ fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
              FB Queue
            </Typography>
          </Stack>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {loading
              ? "Loading queue…"
              : `${totalActionable} item${totalActionable === 1 ? "" : "s"} to process`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncOutlinedIcon />}
            variant="outlined"
            onClick={() => load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Status tabs */}
      <Paper variant="outlined" sx={{ p: 1.25, mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusTab}
            onChange={(_, v) => { if (v) setStatusTab(v); }}
          >
            {STATUS_TABS.map((t) => (
              <ToggleButton key={t.id} value={t.id} sx={{ textTransform: "none", px: 1.5 }}>
                {t.label}
                <Box component="span" sx={{ ml: 1, color: "text.secondary", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                  {counts[t.id] || 0}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            placeholder="Search title or lot…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 84 }}>Image</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Price</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                <TableCell sx={{ width: 130 }}>Updated</TableCell>
                <TableCell sx={{ width: 250 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {counts[statusTab] === 0
                      ? `No items in "${statusTab}" status.`
                      : "No items match your search."}
                  </TableCell>
                </TableRow>
              )}
              {visible.map((row) => {
                const busy = rowBusy[row.lot_id];
                return (
                  <TableRow key={row.lot_id} hover>
                    <TableCell>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={56} />
                    </TableCell>
                    <TableCell>
                      <Stack gap={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>
                          {row.cleaned_title || row.title || "—"}
                        </Typography>
                        <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap sx={{ fontSize: 11 }}>
                          <Box component="span" sx={{ fontFamily: "monospace", color: "text.secondary" }}>{row.lot_id}</Box>
                          {row.current_condition && (
                            <Chip size="small" variant="outlined" label={row.current_condition} sx={{ height: 18, fontSize: 10 }} />
                          )}
                          {row.current_location ? (
                            <Chip size="small" variant="outlined" label={`@ ${row.current_location}`} sx={{ height: 18, fontSize: 10 }} />
                          ) : (
                            <Chip size="small" variant="outlined" color="warning" label="no location" sx={{ height: 18, fontSize: 10 }} />
                          )}
                          {row.listed_ebay && (
                            <Chip size="small" variant="outlined" color="primary" label="on eBay" sx={{ height: 18, fontSize: 10 }} />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {money(row.price)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {formatRelative(row.updated_at)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {(row.status === "queued" || row.status === "error" || row.status === "exported") && (
                          <Tooltip title="Open Chrome and publish this listing to FB now (30–90s).">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={busy === "publish" ? <CircularProgress size={12} color="inherit" /> : <PlayArrowOutlinedIcon fontSize="small" />}
                              disabled={!!busy}
                              onClick={() => publishOne(row)}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              {busy === "publish" ? "Publishing…" : "Publish now"}
                            </Button>
                          </Tooltip>
                        )}
                        {row.status !== "listed" && (
                          <Tooltip title="Mark as listed (use after manual posting)">
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={busy === "listed" ? <CircularProgress size={12} /> : <CheckOutlinedIcon fontSize="small" />}
                              disabled={!!busy}
                              onClick={() => setStatus(row.lot_id, "listed")}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              Listed
                            </Button>
                          </Tooltip>
                        )}
                        {row.status !== "queued" && (
                          <Tooltip title="Send back to queue for the next publish run">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={busy === "queued" ? <CircularProgress size={12} /> : <RestartAltOutlinedIcon fontSize="small" />}
                              disabled={!!busy}
                              onClick={() => setStatus(row.lot_id, "queued")}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              Requeue
                            </Button>
                          </Tooltip>
                        )}
                        {row.status !== "error" && row.status !== "listed" && (
                          <Tooltip title="Mark as error">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={busy === "error" ? <CircularProgress size={12} /> : <ErrorOutlineOutlinedIcon fontSize="small" />}
                              disabled={!!busy}
                              onClick={() => setStatus(row.lot_id, "error")}
                              sx={{ minWidth: 0, px: 1 }}
                            >
                              Error
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove from queue entirely">
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            startIcon={busy === "remove" ? <CircularProgress size={12} /> : <DeleteOutlineOutlinedIcon fontSize="small" />}
                            disabled={!!busy}
                            onClick={() => remove(row.lot_id)}
                            sx={{ minWidth: 0, px: 0.5, color: "text.secondary" }}
                          >
                            Drop
                          </Button>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
